import {
  buildCanonicalRevisionModel,
  type CanonicalRevisionModel,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import { usePageRevisionFamilyCascadeTargets } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamilyCascadeTargets'
import { usePageRevisionFamilyDiscovery } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamilyDiscovery'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type {
  DocumentMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  applyPageUserEditsToRevisionFamilyMembers,
  buildPageRevisionFamilyMembers,
  filterMembersByEffectivePageRevisionFamilyKey,
  type PageRevisionFamilyMember,
  systemDocumentsByBqIdMap,
  uniqueBqDocumentIdsFromPages,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { revisionFamilyEditForPage } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamilyDiscovery'
import { useCallback, useMemo } from 'react'

export type UsePageRevisionFamilyArgs = {
  pageEntry: SystemDocumentPageEntry | undefined
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  currentBqDocumentId: string | undefined
  currentPageNum: number
  enabled?: boolean
}

export type UsePageRevisionFamilyResult = {
  members: PageRevisionFamilyMember[]
  canonicalRevisionModel: CanonicalRevisionModel
  priorEditsByPageEntryId: ReadonlyMap<
    string,
    DocumentPageMetadataUserEditEntry
  >
  documentsByBqId: ReadonlyMap<string, SystemDocumentEntry>
  priorDocumentEditsByBqDocumentId: ReadonlyMap<
    string,
    DocumentMetadataUserEditEntry
  >
  mutatePriorEdits: () => Promise<unknown>
  /** Family candidates and their prior edits are ready for a family save. */
  arePriorEditsReady: boolean
  isLoading: boolean
  familySize: number
  hasRevisionFamily: boolean
}

export function usePageRevisionFamily({
  pageEntry,
  pageUserEdit,
  currentBqDocumentId,
  currentPageNum,
  enabled = true,
}: UsePageRevisionFamilyArgs): UsePageRevisionFamilyResult {
  const {
    pages: familyPages,
    editsByPageId,
    currentFamilyKey,
    mutatePriorEdits,
    areEditsReady,
    isLoading: isLoadingFamilyPages,
  } = usePageRevisionFamilyDiscovery({ pageEntry, pageUserEdit, enabled })

  const documentIds = useMemo(
    () => uniqueBqDocumentIdsFromPages(familyPages),
    [familyPages]
  )

  const { documents, isLoading: isLoadingDocuments } =
    useSystemDocumentsByBqIds(documentIds)

  const members = useMemo(() => {
    if (familyPages.length === 0) return []
    return buildPageRevisionFamilyMembers({
      pages: familyPages,
      currentBqDocumentId,
      currentPageNum,
      documentsByBqId: systemDocumentsByBqIdMap(documents ?? []),
    })
  }, [currentBqDocumentId, currentPageNum, documents, familyPages])

  const documentsByBqId = useMemo(
    () => systemDocumentsByBqIdMap(documents ?? []),
    [documents]
  )

  const {
    priorDocumentEditsByBqDocumentId,
    mutateCascadePriorEdits,
    areCascadePriorEditsReady,
    isCascadeLoading,
  } = usePageRevisionFamilyCascadeTargets({
    documentIds,
    enabled,
  })

  const priorEditsByPageEntryId = useMemo(() => {
    const map = new Map<string, DocumentPageMetadataUserEditEntry>()
    for (const member of members) {
      const edit = revisionFamilyEditForPage(member.systemPage, editsByPageId)
      if (edit) map.set(member.memberEntryId, edit)
    }
    return map
  }, [members, editsByPageId])

  const mutateAllPriorEdits = useCallback(async () => {
    await Promise.all([mutatePriorEdits(), mutateCascadePriorEdits()])
  }, [mutateCascadePriorEdits, mutatePriorEdits])

  const editValuesByPageEntryId = useMemo(() => {
    const map = new Map<
      string,
      DocumentPageMetadataUserEditValues | undefined
    >()
    for (const member of members) {
      if (member.isCurrent && pageUserEdit !== undefined) {
        map.set(
          member.memberEntryId,
          pageUserEdit.entry.data.values ?? undefined
        )
        continue
      }
      map.set(
        member.memberEntryId,
        priorEditsByPageEntryId.get(member.memberEntryId)?.entry.data.values ??
          undefined
      )
    }
    return map
  }, [members, pageUserEdit, priorEditsByPageEntryId])

  const displayMembers = useMemo(() => {
    const effectiveMembers = filterMembersByEffectivePageRevisionFamilyKey({
      members,
      currentFamilyKey,
      editValuesByPageEntryId,
    })
    return applyPageUserEditsToRevisionFamilyMembers({
      members: effectiveMembers,
      editsByPageEntryId: editValuesByPageEntryId,
    })
  }, [currentFamilyKey, editValuesByPageEntryId, members])

  const canonicalRevisionModel = useMemo(
    () => buildCanonicalRevisionModel(members, displayMembers),
    [members, displayMembers]
  )

  const isLoading =
    isLoadingFamilyPages ||
    (documentIds.length > 0 && isLoadingDocuments) ||
    isCascadeLoading

  const arePriorEditsReady = areEditsReady && areCascadePriorEditsReady

  return {
    members: displayMembers,
    canonicalRevisionModel,
    priorEditsByPageEntryId,
    documentsByBqId,
    priorDocumentEditsByBqDocumentId,
    mutatePriorEdits: mutateAllPriorEdits,
    arePriorEditsReady,
    isLoading,
    familySize: displayMembers.length,
    hasRevisionFamily: displayMembers.length > 1,
  }
}
